/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Join Referral Page - Public landing page for referral links
 * Validates the referral code and redirects to registration with the code
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, UserPlus, AlertCircle, CheckCircle, Gift, ArrowRight } from 'lucide-react'
import { getVisitorId } from '../services/campaignService'
import { createLogger } from '../utils/logger'

const logger = createLogger('JoinReferral')

const JoinReferral = () => {
  const { code } = useParams()
  const navigate = useNavigate()

  const [isValidating, setIsValidating] = useState(true)
  const [validation, setValidation] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (code) {
      validateCode()
    } else {
      setError('No referral code provided')
      setIsValidating(false)
    }
  }, [code])

  const validateCode = async () => {
    setIsValidating(true)
    setError(null)

    try {
      // Use unified resolver that checks both referral and campaign codes
      const response = await fetch(`/api/campaigns/resolve/${encodeURIComponent(code)}`)
      const result = await response.json()

      if (result.success && result.data?.valid) {
        setValidation(result.data)

        // If it's a campaign, record a hit and store visitor ID
        if (result.data.type === 'campaign') {
          const visitorId = getVisitorId()
          fetch('/api/campaigns/hit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              campaignCode: code,
              visitorId,
              landingPage: window.location.pathname
            })
          }).catch(() => {}) // fire-and-forget
        }

        // Auto-redirect after a brief delay to show success
        setTimeout(() => {
          navigate(`/register?ref=${encodeURIComponent(code)}`, {
            state: {
              referrerName: result.data.referrerPublicName || result.data.campaignName,
              skipWaitlist: true
            }
          })
        }, 2000)
      } else {
        setError(result.data?.error || 'Invalid referral code')
        setValidation(null)
      }
    } catch (err) {
      logger.error('Error validating code:', err)
      setError('Failed to validate referral code. Please try again.')
      setValidation(null)
    } finally {
      setIsValidating(false)
    }
  }

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Validating your invite...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Just a moment while we verify your referral link
          </p>
        </motion.div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Invalid Referral Link
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>

          <div className="space-y-3">
            <Link
              to="/waitlist"
              className="block w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              Join the Waitlist Instead
            </Link>
            <Link
              to="/login"
              className="block w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  // Success state - brief display before redirect
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6"
        >
          <Gift className="w-10 h-10 text-green-500" />
        </motion.div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You're Invited!</h2>

        {validation?.referrerPublicName && (
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            <span className="font-medium text-primary-600 dark:text-primary-400">
              {validation.referrerPublicName}
            </span>{' '}
            invited you to join Deaveris
          </p>
        )}

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Skip the waitlist - Direct access!</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Redirecting to registration...</span>
        </div>

        <button
          onClick={() => navigate(`/register?ref=${encodeURIComponent(code)}`)}
          className="mt-4 inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
        >
          Continue to registration
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>
    </div>
  )
}

export default JoinReferral
